import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { canHandoffToNativeScheme } from '../utils/inAppBrowser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

/**
 * Detect whether this page is running inside a Chrome Custom Tab
 * (Capacitor Browser overlay) rather than the main Capacitor WebView.
 *
 * When `GuestPaymentMethodPage` opens the Viva checkout via
 * `Browser.open()`, it writes a `allure_native_checkout_pending` flag
 * to localStorage. If that flag is present when this page loads, we
 * know Viva redirected here *inside the Custom Tab* — we must NOT
 * navigate to any app routes (which would render the full app inside
 * Chrome) and instead show a static "done" screen. The user closes the
 * Custom Tab via its ✕ button, and the `onCheckoutClosed` handler in
 * the main WebView reconciles the card.
 */
const isInsideCustomTab = () => {
  try {
    return localStorage.getItem('allure_native_checkout_pending') === '1';
  } catch {
    return false;
  }
};

/**
 * Unified post-Viva-Smart-Checkout landing page.
 *
 * Configure this URL in your Viva dashboard as the Source's Success URL
 * AND Failure URL — Viva appends ?t=<txn>&s=<order>&eventId=...&cancel
 * automatically. We introspect those params and either finalize the
 * card-save flow or show a generic "payment complete" screen, then
 * route the guest back into the app context.
 */
export default function PaymentCompletePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: 'processing', title: 'Confirming…', message: 'Just a moment while we finalise your payment.' });
  const ranRef = useRef(false);
  const [inCustomTab] = useState(isInsideCustomTab);

  // FIRST-ORDER FIX: When Viva Wallet redirects the Chrome Custom Tab
  // back here after processing the card, immediately fire the app's
  // custom URI scheme `allurexp://payment-complete?...`. Android sees
  // the intent, foregrounds the (already-running) Allure app via the
  // MainActivity intent filter, and Chrome Custom Tab closes itself
  // as part of the intent handoff — so the user NEVER sees:
  //   (a) the biometric/passkey prompt Chrome would otherwise pop
  //       because it's still logged into Allure as a different
  //       browser account, and
  //   (b) the confusing "tap ✕ to return to the app" step.
  //
  // In a real web browser (PWA) with the Allure app not installed,
  // this custom scheme silently no-ops after a short beat; we then
  // fall through to the normal in-page finalize/redirect flow below.
  //
  // We fire this synchronously on the first render — BEFORE any
  // finalize/auth calls — so it happens even if the finalize logic
  // would otherwise redirect to /unlock or /login and prompt for
  // passkeys.
  //
  // GUARD: Skip inside third-party in-app WebViews (Instagram, FB,
  // TikTok, Snapchat, WeChat, LinkedIn, Twitter, WhatsApp, Discord).
  // These browsers block custom URI schemes and would leave the user
  // stranded on a blank white page after Apple Pay completes. When
  // this guard skips the handoff, the normal in-page finalize flow
  // below still runs and shows a proper "Card saved" screen.
  const deepLinkFiredRef = useRef(false);
  if (!deepLinkFiredRef.current) {
    deepLinkFiredRef.current = true;
    try {
      const rawQs = window.location.search || '';
      const hasVivaParams = params.get('t') || params.get('s') || params.get('transactionId') || params.get('orderCode') || params.has('cancel');
      if (hasVivaParams && canHandoffToNativeScheme()) {
        // Preserve every Viva query param on the way through the
        // scheme so the native handler has full context.
        const target = `allurexp://payment-complete${rawQs}`;
        window.location.replace(target);
      }
    } catch (_e) { /* ignore — normal render will still happen */ }
  }

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    // Clear the native-checkout flag so it doesn't persist across
    // future sessions. The flag's job is done — we've read it.
    try { localStorage.removeItem('allure_native_checkout_pending'); } catch (_e) { /* noop */ }

    const transactionId = params.get('t') || params.get('transactionId') || '';
    const orderCode = params.get('s') || params.get('orderCode') || '';
    const eventId = params.get('eventId') || '';
    const cancelled = params.has('cancel') || params.get('failed') === '1' || eventId === '-1';

    // Helper: only navigate when we're inside the main WebView / regular
    // browser. Inside a Custom Tab, navigation would render the full app
    // inside Chrome and trigger the passkey unlock screen.
    const safeNavigate = (path, delay) => {
      if (inCustomTab) return; // user will close the Custom Tab manually
      setTimeout(() => navigate(path, { replace: true }), delay);
    };

    if (cancelled || (!transactionId && !orderCode)) {
      setState({
        status: inCustomTab ? 'success' : 'error',
        title: inCustomTab ? 'Done' : 'Payment cancelled',
        message: inCustomTab
          ? 'Tap \u2715 above to return to Allure.'
          : 'No worries — you can try again whenever you\u2019re ready.',
      });
      safeNavigate('/guest/qr', 2400);
      return;
    }

    (async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setState({
          status: 'success',
          title: inCustomTab ? 'Done' : 'Payment received',
          message: inCustomTab
            ? 'Tap \u2715 above to return to Allure.'
            : 'Thanks! Please sign in to continue.',
        });
        safeNavigate('/login', 2200);
        return;
      }

      // Best-effort finalize — works for the card-save flow. If there's no
      // pending card-save attempt (e.g. this was a venue/host payment), the
      // backend returns 4xx and we just show a generic "payment received".
      try {
        await axios.post(
          `${API}/guest/payment-method/finalize`,
          { transaction_id: transactionId, order_code: orderCode },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setState({
          status: 'success',
          title: 'Card saved',
          message: inCustomTab
            ? 'Tap \u2715 above to return to Allure.'
            : 'You can now pay at any Allure partner venue with one tap.',
        });
        safeNavigate('/guest/settings/payment-method', 1800);
      } catch (err) {
        const status = err?.response?.status;

        // Fallback: try the reconcile endpoint. Useful when Viva's redirect
        // dropped the `t` param OR when finalize 404'd but we still have a
        // pending attempt that Viva already successfully charged.
        try {
          const recon = await axios.post(
            `${API}/guest/payment-method/reconcile`,
            {},
            { headers: { Authorization: `Bearer ${token}` } },
          );
          if (recon.data?.reconciled) {
            setState({
              status: 'success',
              title: 'Card saved',
              message: inCustomTab
                ? 'Tap \u2715 above to return to Allure.'
                : 'You can now pay at any Allure partner venue with one tap.',
            });
            safeNavigate('/guest/settings/payment-method', 1800);
            return;
          }
        } catch (_reconErr) {
          // fall through to original handling
        }

        if (status && status >= 400 && status < 500) {
          setState({
            status: 'success',
            title: inCustomTab ? 'Done' : 'Payment complete',
            message: inCustomTab
              ? 'Tap \u2715 above to return to Allure.'
              : 'Your payment was processed successfully.',
          });
          safeNavigate('/guest/qr', 1800);
        } else {
          setState({
            status: 'error',
            title: 'Something went wrong',
            message: inCustomTab
              ? 'Close this window and try again in the app.'
              : (err?.response?.data?.detail || 'We couldn\u2019t finalise your transaction. Please try again.'),
          });
          safeNavigate('/guest/qr', 2400);
        }
      }
    })();
  }, [params, navigate, inCustomTab]);

  const colorMap = {
    processing: 'var(--accent-purple)',
    success: '#1F8E2A',
    error: '#C92016',
  };
  const IconMap = {
    processing: Loader2,
    success: CheckCircle2,
    error: XCircle,
  };
  const Icon = IconMap[state.status];

  return (
    <div
      className="app-shell"
      data-testid="payment-complete-page"
      style={{
        backgroundColor: 'var(--surface)',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <img
        src="/allure-logo.png"
        alt="Allure"
        style={{ width: 110, height: 95, objectFit: 'contain', marginBottom: 14 }}
        data-testid="payment-complete-logo"
      />
      <div
        style={{
          width: '100%',
          maxWidth: 340,
          textAlign: 'center',
          background: 'var(--surface)',
          padding: '28px 22px',
          borderRadius: 22,
          border: '1px solid var(--card-border)',
          boxShadow: 'var(--premium-shadow-main)',
        }}
      >
        <Icon
          size={56}
          color={colorMap[state.status]}
          strokeWidth={1.8}
          className={state.status === 'processing' ? 'animate-spin' : ''}
          data-testid={`payment-complete-icon-${state.status}`}
        />
        <h1
          data-testid="payment-complete-title"
          style={{
            marginTop: 14,
            fontFamily: FONT,
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--text)',
            lineHeight: 1.25,
          }}
        >
          {state.title}
        </h1>
        <p
          data-testid="payment-complete-message"
          style={{
            marginTop: 8,
            fontFamily: FONT,
            fontSize: 15,
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}
        >
          {state.message}
        </p>
      </div>
    </div>
  );
}
